import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
} from "@mui/material";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import {
  StartCampaignFullInitialSendMutation,
  StartCampaignFullInitialSendMutationVariables,
  ScheduleCampaignFullInitialSendMutation,
  ScheduleCampaignFullInitialSendMutationVariables,
  ScheduleCampaignPartialInitialSendMutation,
  ScheduleCampaignPartialInitialSendMutationVariables,
  StartCampaignPartialInitialSendMutation,
  StartCampaignPartialInitialSendMutationVariables,
  EnableTexterSendMutation,
  EnableTexterSendMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikDatePicker from "~frontend/components/forms/FormikDatePicker";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import { FormikTimePicker } from "~frontend/components/forms/FormikTimePicker";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  START_CAMPAIGN_FULL_INITIAL_SEND,
  SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND,
  SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND,
  START_CAMPAIGN_PARTIAL_INITIAL_SEND,
  GET_CAMPAIGN_FOR_VIEW,
  ENABLE_TEXTER_SEND,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { timezoneDisplayName } from "~frontend/util/dateFormat";
import { plural } from "~frontend/util/plural";
import { spacingArray } from "~frontend/util/styling";
import useMutation from "~frontend/util/useMutation";

import { SendDetailsMini } from "../CampaignDetails";
import { VariantOverview } from "../VariantOverview";
import { CampaignForView } from "../types";
import RoiSettingsForm, {
  RoiFormValues,
  roiFormValuesFromCampaign,
} from "./RoiSettingsForm";
import { SendMode } from "./types";

function isPortionMode(sendMode: SendMode) {
  return (
    sendMode === "portion" ||
    sendMode === "schedulePortion" ||
    sendMode === "roiTest" ||
    sendMode === "scheduleRoiTest"
  );
}

function isScheduleMode(sendMode: SendMode) {
  return (
    sendMode === "schedule" ||
    sendMode === "schedulePortion" ||
    sendMode === "scheduleRoiTest"
  );
}

function isRoiTestMode(sendMode: SendMode) {
  return sendMode === "roiTest" || sendMode === "scheduleRoiTest";
}

const DAY_FORMAT = "yyyy-MM-dd";
const TIME_FORMAT = "hh:mm a";

const EQUAL_SPLIT_DISCLAIMER =
  "Note that sending across script variants will roughly split your list equally, but we can't guarantee an exact split.";

const SCHEDULE_SEND_WINDOW_DAYS = 14;

const DATE_ERROR = `Must be within the next ${SCHEDULE_SEND_WINDOW_DAYS} days`;
const TIME_ERROR = "Must be within texting hours";

function remainingTextCounts({
  totalTextCount,
  nLeftToSend,
}: {
  totalTextCount: number;
  nLeftToSend: number;
}) {
  if (totalTextCount === nLeftToSend) {
    return (
      <>
        all <strong>{plural(nLeftToSend, "text", "texts")}</strong>
      </>
    );
  }

  return (
    <>
      the remaining <strong>{plural(nLeftToSend, "text", "texts")}</strong>
    </>
  );
}

const createFormContentForSendMode = (
  isSingleVariant: boolean = false,
  sendMode: SendMode
) => {
  switch (sendMode) {
    case "all":
      return {
        titlePrefix: "Send all Texts for",
        description: (counts: {
          totalTextCount: number;
          nLeftToSend: number;
        }) => (
          <Typography>
            Confirm that the below information is correct, then click “send” to
            send {remainingTextCounts(counts)} for your campaign.
          </Typography>
        ),
        confirmText: "Send all",
      };
    case "portion":
      return {
        titlePrefix: "Send a Portion of",
        description: () => (
          <Typography>
            Enter how many initial messages you want to send for this portion
            and confirm that the below information is correct, then click "send
            portion" to start your campaign.
          </Typography>
        ),
        confirmText: "Send portion",
      };
    case "roiTest":
      return {
        titlePrefix: "Autosend Based on ROI for",
        description: () => {
          const text = isSingleVariant
            ? `Enter how many initial messages you want to send for your test, and 
            set your conditions to send to your full list. If you criteria aren't 
            met when the test is done sending, your campaign will pause sending 
            until the conditions are met.`
            : `Enter how many initial messages you want to send for your test, and
            set your conditions to send the script variant with the highest ROI to
            your full list. If your criteria aren't met when the portion is
              done sending, your campaign will pause sending until the conditions
              are met. Note that you may only run one ROI test per campaign.`;
          return <Typography>{text}</Typography>;
        },
        confirmText: "Begin ROI test",
      };
    case "schedule":
      return {
        titlePrefix: "Schedule Sending",
        description: (counts: {
          totalTextCount: number;
          nLeftToSend: number;
        }) => (
          <Typography>
            Enter your scheduled send time and confirm that the below
            information is correct to send {remainingTextCounts(counts)} for
            your campaign. Your campaign will run from the start time you select
            until it is complete or the end of texting hours.
          </Typography>
        ),
        confirmText: "Schedule send",
      };
    case "schedulePortion":
      return {
        titlePrefix: "Schedule a Portion of",
        description: () => (
          <Typography>
            Enter how many initial messages you want to send for this portion
            and your scheduled send time, confirm that the below information is
            correct, then click "schedule portion" to start your campaign.
          </Typography>
        ),
        confirmText: "Schedule portion",
      };
    case "scheduleRoiTest":
      return {
        titlePrefix: "Schedule Autosend Based on ROI for",
        description: () => {
          const roiFirstSentence = isSingleVariant
            ? `Enter how many initial messages you want to send for your test, and 
            set your conditions to send to your full list`
            : `Enter how many initial messages you want to send for your test, and
            set your conditions to send the script variant with the highest ROI to
            your full list`;
          return (
            <Typography>
              {roiFirstSentence}. If your criteria aren't met when the portion
              is done sending, your campaign will pause sending until the
              conditions are met. Note that you may only run one ROI test per
              campaign.
            </Typography>
          );
        },
        confirmText: "Schedule ROI test",
      };
    case "texterSend":
      return {
        titlePrefix: "Turn on Texter Send for",
        description: (counts: {
          totalTextCount: number;
          nLeftToSend: number;
        }) => (
          <Typography>
            Enter the initial batch size that you would like texters to send at
            a time, then click "start" to enable texters to start sending
            batches of the{" "}
            {counts.totalTextCount === counts.nLeftToSend ? (
              <strong>{plural(counts.nLeftToSend, "text", "texts")}</strong>
            ) : (
              <>
                remaining{" "}
                <strong>{plural(counts.nLeftToSend, "text", "texts")}</strong>
              </>
            )}{" "}
            for your campaign. Texters can continue sending multiple batches as
            long as there are initial texts left to send.
          </Typography>
        ),
        confirmText: "Start",
      };

    default: {
      // This ensures type safety by checking if we've handled all possible SendMode values
      const exhaustiveCheck: never = sendMode;
      throw new Error(`Unhandled send mode: ${exhaustiveCheck}`);
    }
  }
};

export const InitialSendFormDialog = ({
  campaign,
  sendMode,
  handleClose,
}: {
  campaign: CampaignForView;
  sendMode: SendMode;
  handleClose: () => void;
}): ReactElement => {
  const subaccount = useSubaccountAdminConfig()!;

  const [sendFullCampaign] = useMutation<
    StartCampaignFullInitialSendMutation,
    StartCampaignFullInitialSendMutationVariables
  >(START_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start initial send",
  });

  const [sendPartialCampaign] = useMutation<
    StartCampaignPartialInitialSendMutation,
    StartCampaignPartialInitialSendMutationVariables
  >(START_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start initial send",
  });

  const [scheduleFullCampaign] = useMutation<
    ScheduleCampaignFullInitialSendMutation,
    ScheduleCampaignFullInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule initial send",
  });

  const [schedulePartialCampaign] = useMutation<
    ScheduleCampaignPartialInitialSendMutation,
    ScheduleCampaignPartialInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule initial send",
  });

  const [enableTexterSend] = useMutation<
    EnableTexterSendMutation,
    EnableTexterSendMutationVariables
  >(ENABLE_TEXTER_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to enable texter send",
  });

  const campaignTimezone =
    campaign.textingHoursTimezone || subaccount.textingHoursTimezone;

  const [now, setNow] = useState(DateTime.now().setZone(campaignTimezone));

  useEffect(() => {
    const interval = setInterval(
      () => setNow(DateTime.now().setZone(campaignTimezone)),
      60 * 1000
    );
    return () => clearInterval(interval);
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit({
    qty,
    scheduleFor,
    roiTestMinimumRoiPct,
    roiTestMinimumDonationsPerVariant,
    roiTestExcludeDonationsOver,
    roiTestExpiresAt,
    roiTestHoursStart,
    roiTestHoursEnd,
    texterSendBatchSize,
  }: {
    qty: string;
    scheduleFor: DateTime;
    texterSendBatchSize: string;
  } & RoiFormValues) {
    if (subaccount.sendingDisabled) {
      enqueueSnackbar({
        message: "This account cannot send texts at this time.",
      });
      return;
    }

    setIsSubmitting(true);

    const gqlRoiTestConfig: (
      | StartCampaignPartialInitialSendMutationVariables
      | ScheduleCampaignPartialInitialSendMutationVariables
    )["input"]["roiTestConfig"] = isRoiTestMode(sendMode)
      ? {
          minimumRoi: parseInt(roiTestMinimumRoiPct) / 100,
          minimumDonationsPerVariant: parseInt(
            roiTestMinimumDonationsPerVariant
          ),
          excludeDonationsOverCents: Math.floor(
            parseFloat(roiTestExcludeDonationsOver) * 100
          ),
          expiresAt: roiTestExpiresAt,
          testHours: { start: roiTestHoursStart, end: roiTestHoursEnd },
        }
      : null;

    switch (sendMode) {
      case "all":
        await sendFullCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
            },
          },
        });
        break;
      case "portion":
        await sendPartialCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: Number(qty),
            },
          },
        });
        break;
      case "schedule":
        await scheduleFullCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
              scheduleFor,
            },
          },
        });
        break;
      case "schedulePortion":
        await schedulePartialCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: Number(qty),
              scheduleFor,
            },
          },
        });
        break;
      case "roiTest":
        await sendPartialCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: Number(qty),
              roiTestConfig: gqlRoiTestConfig,
            },
          },
        });
        break;
      case "scheduleRoiTest":
        await schedulePartialCampaign({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: Number(qty),
              scheduleFor,
              roiTestConfig: gqlRoiTestConfig,
            },
          },
        });
        break;
      case "texterSend":
        await enableTexterSend({
          variables: {
            input: {
              campaignId: campaign.id,
              texterSendBatchSize: Number(texterSendBatchSize),
            },
          },
        });
        break;
      default:
        sendMode satisfies never;
    }

    setIsSubmitting(false);

    handleClose();
  }

  const nLeftToSend = campaign.quickStats
    ? campaign.quickStats.contactCount - campaign.quickStats.initialSentCount
    : 0;

  const isSingleVariant =
    campaign.scriptVariants.filter(sv => !!sv.deletedAt).length === 1;
  const { titlePrefix, description, confirmText } =
    createFormContentForSendMode(isSingleVariant, sendMode);

  const initialRoiTestFormValues = useMemo(() => {
    if (isRoiTestMode(sendMode) && campaign.roiTestConfig) {
      return roiFormValuesFromCampaign(campaign, subaccount);
    } else {
      return {
        roiTestMinimumRoiPct: "",
        roiTestMinimumDonationsPerVariant: "",
        roiTestExcludeDonationsOver: "",
        roiTestExpiresAt: null,
        roiTestHoursStart:
          campaign.textingHoursStart ?? subaccount.textingHoursStart,
        roiTestHoursEnd: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
      };
    }
  }, [sendMode, campaign, subaccount]);

  const validateDate = (dateTime: DateTime) => {
    return (
      dateTime.toMillis() > now.toMillis() &&
      dateTime.toMillis() <
        now.plus({ days: SCHEDULE_SEND_WINDOW_DAYS }).endOf("day").toMillis()
    );
  };

  const validateTime = (dateTime: DateTime) => {
    const dateTimeInIso = dateTime.toISOTime({
      includeOffset: false,
      suppressMilliseconds: true,
    })!;
    return (
      dateTimeInIso >=
        (campaign.textingHoursStart || subaccount.textingHoursStart) &&
      dateTimeInIso <= (campaign.textingHoursEnd || subaccount.textingHoursEnd)
    );
  };

  const [dateErrorText, setDateErrorText] = useState("");
  const [timeErrorText, setTimeErrorText] = useState("");

  return (
    <FormikDialog
      open={true}
      onClose={!isSubmitting ? handleClose : () => {}}
      maxWidth="md"
      validateOnMount={true}
      initialValues={{
        sendMode,
        qty: "",
        texterSendBatchSize: "",

        ...initialRoiTestFormValues,

        scheduleFor: now
          .set({
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .plus({ hour: 1 }),
      }}
      initialTouched={{ scheduleFor: true }}
      validationSchema={Yup.object().shape({
        qty: Yup.number().when("sendMode", {
          is: (val: SendMode) => isPortionMode(val),
          then: Yup.number()
            .typeError("Quantity must be a number")
            .required("Quantity is required")
            .min(1, "Quantity must be at least 1")
            .max(
              nLeftToSend,
              `Only ${nLeftToSend.toLocaleString()} left to send`
            ),
          otherwise: Yup.number().notRequired(),
        }),
        texterSendBatchSize: Yup.number().when("sendMode", {
          is: (val: SendMode) => val === "texterSend",
          then: Yup.number()
            .typeError("Batch size must be a number")
            .required("Batch size is required")
            .min(1, "Batch size must be at least 1"),
          otherwise: Yup.number().notRequired(),
        }),
        scheduleFor: Yup.mixed().when("sendMode", {
          is: (val: SendMode) => isScheduleMode(val),
          then: Yup.mixed()
            .test("future", DATE_ERROR, validateDate)
            .test("texting hours", TIME_ERROR, validateTime),
        }),
        roiTestMinimumRoiPct: isRoiTestMode(sendMode)
          ? Yup.number()
              .typeError("Minimum ROI must be a number")
              .required("Minimum ROI is required")
              .positive("Minimum ROI must be positive")
              .integer("Minimum ROI must be a whole number")
          : Yup.number(),
        roiTestMinimumDonationsPerVariant: Yup.number()
          .typeError("Minimum donations must a be a number")
          .integer("Minimum donations must be a whole number")
          .positive("Minimum donations must be positive"),
        roiTestExcludeDonationsOver: Yup.number()
          .typeError("Maximum donation must be a number")
          .positive("Maximum donation must be a positive number"),
        roiTestExpiresAt: Yup.mixed()
          .when("sendMode", {
            is: (val: SendMode) => isScheduleMode(val),
            then: Yup.mixed().test(
              "after-send",
              "Must be a valid date after sending",
              (val: DateTime, ctx) =>
                !val || val.toMillis() > ctx.parent.scheduleFor.toMillis()
            ),
          })
          .test(
            "future",
            "Must be a valid date in the future",
            (val: DateTime) => !val || val.toMillis() > now.toMillis()
          ),
        roiTestHoursStart: Yup.mixed(), // TODO
        roiTestHoursEnd: Yup.mixed(), // TODO
      })}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isValid }) => (
        <>
          <DialogTitle>
            {titlePrefix} {campaign.name}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            }}
          >
            {subaccount.sendingDisabled && (
              <Box
                sx={{
                  padding: "10px",
                  backgroundColor: palette.lightOrange,
                  width: "100%",
                }}
              >
                <strong>Warning:</strong> This account cannot send texts at this
                time. Please reach out to{" "}
                <a
                  style={{
                    color: palette.blue,
                    textDecorationColor: palette.blue,
                  }}
                  href="mailto:support@scaletowin.com"
                >
                  support@scaletowin.com
                </a>
                .
              </Box>
            )}
            <Box>
              {description({
                totalTextCount: campaign.quickStats?.contactCount ?? 0,
                nLeftToSend,
              })}
            </Box>
            {!isSingleVariant && <Box>{EQUAL_SPLIT_DISCLAIMER}</Box>}
            {isPortionMode(values.sendMode) && (
              <>
                <FormikTextField
                  name="qty"
                  label="Texts to Send"
                  bottomLabel={
                    isRoiTestMode(sendMode)
                      ? "Your campaign will send this many tests, then wait to see if the variant meets your ROI criteria"
                      : "Your campaign will stop sending after this amount of texts"
                  }
                  fullWidth
                />
                {isRoiTestMode(sendMode) && (
                  <Card
                    variant="outlined"
                    sx={{ padding: theme.spacing(3), overflow: "visible" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ paddingBottom: theme.spacing(2) }}
                    >
                      Set your conditions to send the script variant with the
                      highest ROI to your full list.
                    </Typography>
                    <RoiSettingsForm
                      campaign={campaign}
                      isSingleVariant={isSingleVariant}
                    />
                  </Card>
                )}
              </>
            )}

            {isScheduleMode(values.sendMode) && (
              <FlexBox justifyContent="space-between" gap={3}>
                <FormikDatePicker
                  fullWidth
                  label="Send Date"
                  name="scheduleFor"
                  timezone={campaignTimezone}
                  format={DAY_FORMAT}
                  onChange={async (newValue: DateTime) => {
                    if (newValue?.isValid) {
                      await setFieldValue(
                        "scheduleFor",
                        values.scheduleFor.set({
                          year: newValue.year,
                          month: newValue.month,
                          day: newValue.day,
                        })
                      );
                    }
                  }}
                  onError={async (newError: string) => {
                    if (newError) {
                      setDateErrorText(DATE_ERROR);
                    } else {
                      setDateErrorText("");
                    }
                  }}
                  shouldDisableDate={value => !validateDate(value)}
                  customError={{ errorText: dateErrorText }}
                />

                <FormikTimePicker
                  fullWidth
                  label={`Send Time (${timezoneDisplayName(campaignTimezone)})`}
                  name="scheduleFor"
                  timezone={campaignTimezone}
                  format={TIME_FORMAT}
                  onChange={async (newValue: DateTime) => {
                    if (newValue?.isValid) {
                      await setFieldValue(
                        "scheduleFor",
                        values.scheduleFor.set({
                          hour: newValue.hour,
                          minute: newValue.minute,
                        })
                      );
                    }
                  }}
                  onError={async (newError: string) => {
                    if (newError) {
                      setTimeErrorText(TIME_ERROR);
                    } else {
                      setTimeErrorText("");
                    }
                  }}
                  shouldDisableTime={value => !validateTime(value)}
                  customError={{ errorText: timeErrorText }}
                />
              </FlexBox>
            )}

            {values.sendMode === "texterSend" && (
              <FormikTextField
                name="texterSendBatchSize"
                label="Batch Size for Initial Sends"
                bottomLabel="The number of initial texts a texter can send at a time"
                fullWidth
              />
            )}

            <Box
              style={{
                backgroundColor: palette.lightestGray,
                padding: spacingArray([1, 3, 3, 3]),
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(2),
              }}
            >
              <SendDetailsMini campaign={campaign} />

              <VariantOverview
                campaignId={campaign.id}
                scriptVariants={campaign.scriptVariants.filter(
                  variant => !variant.deletedAt
                )}
                sampleContactData={
                  formatContactData({
                    contactData: JSON.parse(
                      campaign.sampleContact?.data ?? "{}"
                    ),
                    campaignContactFields: campaign.contactFields,
                  }).formattedData
                }
                showFieldExplanation={false}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              padding: spacingArray([0, 3, 3]),
            }}
          >
            <Button disabled={isSubmitting} onClick={handleClose}>
              Cancel
            </Button>
            <FormikSubmitButton
              disabled={isSubmitting || !isValid || subaccount.sendingDisabled}
              variant="contained"
              submitText={confirmText}
            />
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
};
